/**
 * Versioned messages between the React calendar and its persistent scheduling Worker.
 * Payloads contain private authorized snapshots, kept only in memory. Timings distinguish
 * WASM initialization, canonical compute, and the parent thread's request-to-reply latency.
 * @module apps/web/src/lib/schedulingProtocol
 */
import type { GhostBlock } from '@fluxability/types';

/** Available local scheduling backends; GPU never changes objective arithmetic. */
export type SchedulingBackend = 'auto' | 'cpu' | 'webgpu';

/** Read-only scheduling request; warmup never receives user records. */
export interface SchedulingWorkerRequest {
  /** Unique correlation ID; never reused for a retry. */
  id: string;
  /** Initialize WASM or compute a canonical advisory result. */
  kind: 'warm' | 'solve' | 'ghosts';
  /** Auto uses measured WASM performance; webgpu forces the diagnostic integer GPU stage. */
  backend?: SchedulingBackend;
  /** Canonical snapshot JSON, present only for solve messages. */
  input?: string;
  /** Exact server-selected planning week, required only by the independent ghost preview. */
  weekStart?: string;
}

/** Result of the existing seven-day, at-most-ten soft-capacity ghost algorithm; never a full solve. */
export interface SchedulingGhostPreview {
  /** Snapshot identity echoed by the isolated Worker. */
  job_id: string;
  /** Canonical snapshot revision echoed by the isolated Worker. */
  revision: number;
  /** Exact server-selected week used by preview_ghost_blocks. */
  week_start: string;
  /** Locally computed suggestions, still requiring server validation when confirmed. */
  blocks: GhostBlock[];
}

/** Validate the full ghost shape at the WASM boundary without dropping cost, confidence or explanation. */
export function parseSchedulingGhostBlocks(value: unknown): GhostBlock[] {
  if (!Array.isArray(value) || value.length > 10) throw new Error('Invalid browser suggestions.');
  for (const block of value) {
    if (
      !block ||
      typeof block !== 'object' ||
      typeof block.id !== 'string' ||
      !block.id ||
      typeof block.task_id !== 'string' ||
      !block.task_id ||
      typeof block.proposed_start !== 'string' ||
      !Number.isFinite(Date.parse(block.proposed_start)) ||
      typeof block.proposed_end !== 'string' ||
      Date.parse(block.proposed_end) <= Date.parse(block.proposed_start) ||
      !Number.isFinite(Date.parse(block.proposed_end)) ||
      typeof block.cost !== 'number' ||
      !Number.isFinite(block.cost) ||
      typeof block.confidence !== 'number' ||
      !Number.isFinite(block.confidence) ||
      typeof block.reason !== 'string'
    )
      throw new Error('Invalid browser suggestion fields.');
  }
  return value as GhostBlock[];
}

/** Canonical staged result with provenance; no field certifies durable persistence. */
export interface SchedulingPreview {
  /** Server job whose pending batch was captured. */
  job_id: string;
  /** Exact canonical revision used by this preview. */
  revision: number;
  /** Per-activity canonical feasibility result. */
  outcomes: Record<string, 'scheduled' | 'optimized' | 'blocked'>;
  /** Full replacement activity documents, converted by the existing task adapter. */
  changes: unknown[];
}

/** Correlated local result; errors require parent cancellation or bounded lease expiry. */
export interface SchedulingWorkerReply {
  /** Correlation ID echoed from the request. */
  id: string;
  /** Canonical advisory changes, absent on warmup or error. */
  result?: SchedulingPreview;
  /** Full ghost-preview result, separate from full-solve activity changes and never persisted here. */
  ghosts?: SchedulingGhostPreview;
  /** Local WASM initialization time, including module download on a cold Worker. */
  initializationMs?: number;
  /** Time of the synthetic warm-up solve run by a `warm` request; absent on other requests. */
  warmUpMs?: number;
  /** Canonical service compute time including its JSON boundary. */
  computeMs?: number;
  /** Backend that actually classified at least one calendar; all search remains local WASM. */
  backend?: 'wasm' | 'wasm-webgpu';
  /** Total GPU classification time, including upload, execution and readback. */
  gpuMs?: number;
  /** Number of boundary classifications actually consumed by the Rust solver. */
  gpuClassifications?: number;
  /** Local failure; the parent clears provisional placements and offers an explicit retry. */
  error?: string;
}

/** Parent-thread reply augmented with its measured request-to-receipt latency. */
export interface SchedulingWorkerResponse extends SchedulingWorkerReply {
  /** Elapsed parent-thread milliseconds from posting through correlated receipt. */
  roundTripMs: number;
}

/** One client-selected interval. The server independently checks its task, duration and legality. */
export interface SchedulingProposalChunk {
  /** Inclusive UTC ISO timestamp copied from the local canonical result. */
  start: string;
  /** Exclusive UTC ISO timestamp copied from the local canonical result. */
  end: string;
}

/** Narrow placement proposal; no client-owned metadata or objective values become authoritative. */
export interface SchedulingProposal {
  /** Selected activities with proposed UTC chunks; this runtime currently produces one per activity. */
  placements: Array<{
    /** Existing selected activity identifier, checked against the server lease. */
    task_id: string;
    /** Ordered proposed intervals for the activity. */
    chunks: SchedulingProposalChunk[];
  }>;
  /** Selected activities for which the local solver found no placement. */
  unplaced_ids: string[];
}

/** Placement fields projected from a local full document before narrow proposal validation. */
interface SchedulingDocumentFields {
  /** Inclusive UTC timestamp, validated before constructing a proposal. */
  do_date?: unknown;
  /** Exclusive UTC timestamp, validated before constructing a proposal. */
  do_date_end?: unknown;
}

/** Extract only UTC placements and explicit blocked IDs, rejecting missing or malformed staged records.
 * The caller supplies job/revision/lease provenance separately. This projection cannot authorize a save.
 */
export function schedulingProposalFromPreview(preview: SchedulingPreview): SchedulingProposal {
  const documents = new Map<string, SchedulingDocumentFields>();
  for (const change of preview.changes) {
    if (!change || typeof change !== 'object') throw new Error('Invalid scheduling change.');
    const document = change as {
      /** Activity identifier from canonical WASM output. */ id?: unknown;
      /** UTC placement fields, still subject to admission checks. */ core_fields?: SchedulingDocumentFields;
    };
    if (typeof document.id !== 'string' || !document.core_fields || documents.has(document.id))
      throw new Error('Invalid or duplicate scheduling change.');
    documents.set(document.id, document.core_fields);
  }
  const proposal: SchedulingProposal = { placements: [], unplaced_ids: [] };
  for (const [id, outcome] of Object.entries(preview.outcomes)) {
    if (outcome === 'blocked') {
      proposal.unplaced_ids.push(id);
      continue;
    }
    if (outcome !== 'scheduled' && outcome !== 'optimized')
      throw new Error('Invalid scheduling outcome.');
    const fields = documents.get(id);
    const start = fields?.do_date;
    const end = fields?.do_date_end;
    if (
      typeof start !== 'string' ||
      typeof end !== 'string' ||
      !Number.isFinite(Date.parse(start)) ||
      !Number.isFinite(Date.parse(end)) ||
      Date.parse(end) <= Date.parse(start)
    )
      throw new Error('Scheduling result is missing a valid placement.');
    proposal.placements.push({ task_id: id, chunks: [{ start, end }] });
  }
  return proposal;
}
